import { Popover } from "@headlessui/react";
import { FC, useState } from "react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  TextArea
} from "shared/components";
import { DotsHorizontal } from "untitledui-js-base";
import { ApplicationNote } from "./types";
import addressApi from "api/landlord/address";

import { getErrorMessage } from "utils/error";
import moment from "moment";
import { toast } from "react-toastify";

type Props = {
  note: ApplicationNote;
  applicationId: number;
  refetchNotes: () => void;
};

export const Note: FC<Props> = ({ applicationId, note, refetchNotes }) => {
  const [editMode, setEditMode] = useState<boolean>(false);
  const [newMessage, setNewMessage] = useState<string>(note.message);

  const buttonClassName =
    "w-full flex px-4 py-2.5 !text-base !text-content-1000 !font-medium hover:bg-alpha-40";
  return (
    <div className="border-alpha-60 animate-appear mx-6 border-b py-7">
      <div className="mb-2 flex justify-between">
        <div className="flex items-center gap-3">
          <p className="font-bold text-content-700">{note.author.name}</p>
          <p className="text-xs text-content-400">
            {note.updated_at
              ? moment(note.updated_at).format("DD MMM, YYYY [at] HH:mm")
              : moment(note.created_at).format("DD MMM, YYYY [at] HH:mm")}
          </p>
        </div>
        <Popover className="relative">
          <Popover.Button>
            <IconButton
              variant={IconButtonVariant.BASE}
              size={IconButtonSize.SM}
              borderLess
              className="bg-transparent hover:bg-alpha-40"
              Icon={DotsHorizontal}
            />
          </Popover.Button>
          <Popover.Panel className="absolute right-0 z-10 mt-2 w-[140px] rounded-lg border border-alpha-100 bg-background-1000 shadow-xl">
            <Popover.Button className="w-full">
              <Button
                size={ButtonSize.SM}
                variant={ButtonVariant.TEXT}
                onClick={() => setEditMode(true)}
                className={buttonClassName}
              >
                Edit note
              </Button>
            </Popover.Button>
            <Popover.Button className="w-full">
              <Button
                size={ButtonSize.SM}
                variant={ButtonVariant.TEXT}
                onClick={async () => {
                  try {
                    await addressApi.deleteApplicationNote(
                      Number(applicationId),
                      note.id
                    );
                    refetchNotes();
                  } catch (err) {
                    toast.error(getErrorMessage(err));
                  }
                }}
                className={`${buttonClassName} border-t border-alpha-40 !text-danger-500`}
              >
                Delete note
              </Button>
            </Popover.Button>
          </Popover.Panel>
        </Popover>
      </div>
      {editMode ? (
        <>
          <TextArea
            id="note"
            placeholder="You can write a note here"
            value={newMessage}
            rows={4}
            onChange={(e) => setNewMessage(e.target.value)}
          />
          <div className="mt-2 flex justify-end gap-2">
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              onClick={() => setEditMode(false)}
            >
              Cancel
            </Button>
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              onClick={async () => {
                try {
                  await addressApi.editApplicationNote(
                    applicationId,
                    note.id,
                    newMessage
                  );
                  refetchNotes();
                  setEditMode(false);
                } catch (err) {
                  toast.error(getErrorMessage(err));
                }
              }}
            >
              Save
            </Button>
          </div>
        </>
      ) : (
        <p className="text-sm text-content-1000">{note.message}</p>
      )}
    </div>
  );
};
